import React, { useState } from "react";
import {
    Badge, Box, Button, Flex, Icon, Spinner, Switch, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdNotificationsActive, MdRefresh } from "react-icons/md";
import NotificationItem from "./NotificationItem";
import useAdminNotifications from "./useAdminNotifications";

/**
 * Renders the page header with the unread badge and the list controls.
 * @param {Object} props - counters and handlers driving the header
 * @returns {React.ReactElement}
 */
function PageHeader({ unread, loading, onRefresh, onMarkAll }) {
    const badgeBg = useColorModeValue("brand.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    return (
        <Flex align="center" justify="space-between" gap="12px" mb="20px" wrap="wrap">
            <Flex align="center" gap="12px">
                <Flex align="center" justify="center" w="44px" h="44px" borderRadius="12px" bg={badgeBg} flexShrink="0">
                    <Icon as={MdNotificationsActive} w="24px" h="24px" color="brand.500" />
                </Flex>
                <Box>
                    <Flex align="center" gap="8px">
                        <Text fontSize="lg" fontWeight="700" color={titleColor}>Notifications</Text>
                        {unread > 0 && (
                            <Badge colorScheme="red" borderRadius="6px" px="8px">{unread}</Badge>
                        )}
                    </Flex>
                    <Text fontSize="sm" color={subColor}>
                        Incidents signalés par l'API : découverts de trésorerie, échecs de
                        répartition. Chaque administrateur a sa propre liste.
                    </Text>
                </Box>
            </Flex>
            <Flex align="center" gap="10px">
                {unread > 0 && (
                    <Button size="sm" variant="ghost" onClick={onMarkAll}>Tout marquer comme lu</Button>
                )}
                <Button size="sm" leftIcon={<Icon as={MdRefresh} />} onClick={onRefresh} isLoading={loading}>
                    Actualiser
                </Button>
            </Flex>
        </Flex>
    );
}

/**
 * Renders the loading, error and empty states of the list.
 * @param {Object} props - loading flag, error payload and emptiness flag
 * @returns {React.ReactElement|null}
 */
function ListState({ loading, error, isEmpty, unreadOnly }) {
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (loading) return <Flex justify="center" py="40px"><Spinner color="brand.500" /></Flex>;
    if (error) {
        return (
            <Text fontSize="sm" color="red.400" py="20px">
                {typeof error === "string" ? error : "Impossible de charger les notifications"}
            </Text>
        );
    }
    if (isEmpty) {
        return (
            <Text fontSize="sm" color={hintColor} py="20px">
                {unreadOnly
                    ? "Aucune notification non lue."
                    : "Aucun incident signalé pour le moment."}
            </Text>
        );
    }
    return null;
}

/**
 * Admin notification centre: lists the incidents raised by the API and lets
 * an admin acknowledge them or jump to the screen that explains them.
 * @returns {React.ReactElement}
 */
function Notifications() {
    const { items, unread, loading, error, refresh, markRead, markAllRead } = useAdminNotifications();
    const [unreadOnly, setUnreadOnly] = useState(false);
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const cardBg = useColorModeValue("white", "navy.700");

    const visible = unreadOnly ? items.filter((row) => !row.read) : items;
    const isEmpty = !loading && !error && visible.length === 0;

    return (
        <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
            <PageHeader unread={unread} loading={loading} onRefresh={refresh} onMarkAll={markAllRead} />
            <Box bg={cardBg} borderRadius="16px" p="20px">
                <Flex align="center" gap="10px" mb="16px">
                    <Switch
                        size="sm"
                        isChecked={unreadOnly}
                        onChange={(event) => setUnreadOnly(event.target.checked)}
                    />
                    <Text fontSize="sm" color={labelColor}>Non lues uniquement</Text>
                </Flex>
                <ListState loading={loading} error={error} isEmpty={isEmpty} unreadOnly={unreadOnly} />
                {visible.map((notification) => (
                    <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkRead={markRead}
                    />
                ))}
            </Box>
        </Box>
    );
}

export default Notifications;
